// -----------------------------------------------------------------------------
// <copyright file="popover.directive.ts" company="ecoInsight, Inc.">
// 	Copyright © ecoInsight, Inc. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PopoverService } from './popover.service';

@Directive({
  selector: '[fsPopoverTrigger]',
  standalone: false,
})
export class PopoverDirective implements OnDestroy, OnInit {
  @Input()
  fsPopoverTrigger!: TemplateRef<object>;

  @Input()
  closeOnClickOutside = false;

  @Input()
  disableAutoTrigger = false;

  @Input()
  position: ConnectedPosition = {
    originX: 'start',
    originY: 'bottom',
    overlayX: 'start',
    overlayY: 'bottom',
  };

  @Input()
  offsetY = 10;
  @Input() offsetX = 0;

  @Input()
  hasBackdrop = true;

  @Input()
  closeOnScroll = true;

  @Input() actAsTooltip = false;

  @Input() selectDropdownId = null;

  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onClickOutside = new EventEmitter();

  private unsubscribe = new Subject();
  private overlayRef!: OverlayRef;

  constructor(
    private elementRef: ElementRef,
    private overlay: Overlay,
    private vcr: ViewContainerRef,
    private popoverService: PopoverService,
    private renderer: Renderer2,
  ) {}

  ngOnInit() {
    this.popoverService
      .getState()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((resp) => {
        if (resp) {
          this.detachOverlay();
        }
      });
    this.popoverService
      .getPositionState()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((resp) => {
        if (resp) {
          this.setPosition();
        }
      });
  }

  ngOnDestroy(): void {
    this.detachOverlay();
    this.disposeOverlay();
    this.unsubscribe.next(undefined);
    this.unsubscribe.complete();
  }

  @HostListener('click') clickou() {
    if (!this.disableAutoTrigger && !this.actAsTooltip) {
      this.attachOverlay();
    }
  }

  @HostListener('mouseenter') mouseover() {
    if (!this.disableAutoTrigger && this.actAsTooltip) {
      // close the fs-select dropdown when tooltip is active
      if (this.selectDropdownId) {
        const eulInputs = document.getElementsByClassName(
          'ei-select-input' + this.selectDropdownId,
        );
        this.popoverService.close();
        const eul = document.getElementById('eiselectdd');
        if (eulInputs && eulInputs.length > 0 && eul) {
          Array.prototype.forEach.call(eulInputs, function (eulInput) {
            eulInput.blur();
          });
          if (eul) {
            eul.remove();
          }
        }
      }
      this.attachOverlay();
    }
  }
  @HostListener('mouseleave')
  hide() {
    if (this.actAsTooltip) {
      this.detachOverlay();
    }
  }

  private createOverlay(): void {
    const scrollStrategy = this.closeOnScroll
      ? this.overlay.scrollStrategies.close()
      : this.overlay.scrollStrategies.block();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([this.position]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: this.hasBackdrop,
      backdropClass: '',
    });

    this.overlayRef
      .backdropClick()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        if (this.closeOnClickOutside) {
          this.detachOverlay();
        }
      });
    this.overlayRef
      .outsidePointerEvents()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        if (this.closeOnClickOutside && !this.hasBackdrop) {
          this.onClickOutside.emit(true);
          this.detachOverlay();
        }
      });
  }

  private attachOverlay(): void {
    if (!this.overlayRef) {
      this.createOverlay();
    }
    if (!this.overlayRef.hasAttached()) {
      this.renderer.addClass(this.elementRef.nativeElement, 'fs-popover-active');
      const periodSelectorPortal = new TemplatePortal(this.fsPopoverTrigger, this.vcr);
      const ref = this.overlayRef.attach(periodSelectorPortal);
      setTimeout(() => {
        this.setPosition(ref.rootNodes[0]);
      }, 10);
    }
  }

  showPopover() {
    this.attachOverlay();
  }

  closePopover() {
    this.detachOverlay();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setPosition(templateElement?: HTMLElement) {
    if (!this.overlayRef) return;

    const hostPos = this.elementRef.nativeElement.getBoundingClientRect();
    const templateElementPos = document
      .getElementsByClassName('popover-container')[0]
      ?.getBoundingClientRect();
    const viewPortHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0);
    const position =
      Math.round(hostPos?.top) +
      Math.round(hostPos?.height) +
      (templateElementPos?.height || 0) +
      10;

    const obj: ConnectedPosition = {
      ...this.position,
      offsetX: this.offsetX,
    };
    if (position > viewPortHeight) {
      obj.overlayY = 'bottom';
      obj.originY = 'top';
      obj.offsetY = -this.offsetY;
    } else {
      obj.overlayY = 'top';
      obj.offsetY = this.offsetY;
    }
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([obj]);
    this.overlayRef.updatePositionStrategy(positionStrategy);
  }

  private detachOverlay(): void {
    if (this.overlayRef?.hasAttached()) {
      this.renderer.removeClass(this.elementRef.nativeElement, 'fs-popover-active');
      this.overlayRef.detach();
    }
  }

  private disposeOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.dispose();
    }
  }
}
